(() => {
  'use strict';

  const installed = Symbol.for('mw.tepo.grouped.v2');
  const isTargetPage = () => {
    const data = window.mwMarketingOffersLiquidData;
    const productId = Number(data?.productId);
    return data?.page === 'product' && Number.isSafeInteger(productId) && productId > 0
      && typeof data.productHandle === 'string'
      && location.pathname.replace(/\/$/, '').endsWith(`/products/${data.productHandle}`)
      && Array.isArray(data.parentProductIds)
      && data.parentProductIds.some(id => Number(id) === productId);
  };
  if (window[installed]) return;
  window[installed] = true;

  const previousFetch = window.fetch;
  let pending = false;
  const failure = (description) => new Response(JSON.stringify({
    status: 422,
    message: 'Unable to add bundle',
    description,
  }), { status: 422, headers: { 'Content-Type': 'application/json' } });

  const unavailable = 'This bundle could not be added. Please refresh the page and try again.';

  function selectedItems(helper, offer) {
    const widgets = document.querySelectorAll('mw-bundle-product');
    const root = widgets.length === 1 ? widgets[0].shadowRoot : null;
    const triggerIds = [String(helper.product.id), ...Object.keys(helper.product.variants || {})];
    const target = offer.items?.find(item => triggerIds.includes(String(item.id)));
    const components = target ? target.offers : offer.items?.map(item =>
      item.offers?.find(component => String(component.id) === String(item.target?.id))
    ).filter(Boolean);
    if (!root || !components?.length || offer.qty_type !== 'CUSTOMIZABLE') throw new Error(unavailable);
    const rows = [...root.querySelectorAll('.BundleProduct__Item:not(.BundleProduct__Head)')];
    if (rows.length !== components.length) throw new Error(unavailable);
    const used = new Set();
    const items = components.map((component) => {
      const product = helper.productMap?.get(Number(component.product_id));
      if (!product || product.variants?.length !== 1) throw new Error(unavailable);
      const matches = rows.filter(row => [...row.querySelectorAll('a[href]')].some(link =>
        new URL(link.href, location.href).pathname.replace(/\/$/, '').endsWith(`/products/${product.handle}`)));
      if (matches.length !== 1 || used.has(matches[0])) throw new Error(unavailable);
      const row = matches[0];
      used.add(row);
      const inputs = row.querySelectorAll('input[type="number"]');
      if (inputs.length !== 1) throw new Error(unavailable);
      const input = inputs[0];
      const quantity = Number(input.value);
      if (input.value.trim() === '' || !input.validity.valid || !Number.isSafeInteger(quantity)
        || quantity < Number(component.quantity_min) || quantity > Number(component.quantity)) {
        input.reportValidity();
        throw new Error('Please check the selected bundle quantities.');
      }
      const variant = product.variants[0];
      if (!Number.isSafeInteger(Number(variant.id)) || (quantity > 0 && !variant.available)) throw new Error(unavailable);
      return { id: variant.id, quantity };
    }).filter(item => item.quantity > 0);
    if (!items.length) throw new Error('Please select at least one bundle item.');
    return items;
  }

  window.fetch = async function (resource, options) {
    const forward = () => Reflect.apply(previousFetch, this, [resource, options]);
    if (!isTargetPage() || options?.byTcustomizer !== true
      || String(options.method).toUpperCase() !== 'POST') return forward();
    const url = new URL(resource instanceof Request ? resource.url : String(resource), location.href);
    if (url.origin !== location.origin || !/\/cart\/add(?:\.js)?\/?$/.test(url.pathname)) return forward();

    const helper = window.mwMarketingOffersHelper;
    const offer = helper?.offers?.bundle_product;
    if (!helper?.ready || helper.product?.handle !== window.mwMarketingOffersLiquidData.productHandle
      || Number(helper.product.id) !== Number(window.mwMarketingOffersLiquidData.productId)
      || !Number.isSafeInteger(Number(offer?.id)) || Number(offer.id) <= 0
      || offer.type !== 'bundle' || offer.sub_type !== 'PRODUCT' || !offer.status
      || Number(offer.parent_product_id) !== Number(helper.product.id)) return forward();

    if (!(options.body instanceof FormData)) return failure(unavailable);
    const entries = [...options.body.entries()];
    const parentIds = new Set(Object.keys(helper.product.variants || {}));
    const idFields = entries.filter(([key]) => /^items\[\d+\](?:\[id\]|id)$/.test(key));
    const parentFields = idFields.filter(([, value]) => parentIds.has(String(value)));
    if (!parentFields.length) return forward();
    if (parentFields.length !== 1 || idFields.length !== 1) return failure(unavailable);
    if (pending) return failure('Your bundle is already being added. Please wait.');
    pending = true;

    try {
      const index = parentFields[0][0].match(/^items\[(\d+)\]/)[1];
      const prefix = `items[${index}]`;
      const properties = [];
      const body = new FormData();
      for (const [key, value] of entries) {
        if (!key.startsWith('items[')) {
          if (key === 'id' || key === 'quantity' || key === 'selling_plan') throw new Error(unavailable);
          body.append(key, value);
          continue;
        }
        if (!key.startsWith(prefix)) throw new Error(unavailable);
        const field = key.slice(prefix.length);
        if (/selling_plan/.test(field) && String(value)) throw new Error(unavailable);
        if (field.startsWith('[properties')) {
          if (/_(?:addonVariant|parentKey|groupId)|charge_price|pricePerLetter/i.test(field)) throw new Error(unavailable);
          if (!field.includes('_mw_marketing_offers_offer_id')) properties.push([field, value]);
        } else if (!['[id]', 'id', '[quantity]', 'quantity', '[form_type]', '[utf8]', '[product-id]', '[section-id]', '[selling_plan]'].includes(field)) {
          throw new Error(unavailable);
        }
      }
      const items = selectedItems(helper, offer);
      items.forEach((item, i) => {
        body.append(`items[${i}][id]`, String(item.id));
        body.append(`items[${i}][quantity]`, String(item.quantity));
        properties.forEach(([field, value]) => body.append(`items[${i}]${field}`, value));
        body.append(`items[${i}][properties][_mw_marketing_offers_offer_id]`, String(offer.id));
      });
      const headers = new Headers(options.headers);
      headers.delete('Content-Type');
      return await Reflect.apply(previousFetch, this, [resource, { ...options, headers, body }]);
    } catch (error) {
      if (error instanceof TypeError || error?.name === 'AbortError') throw error;
      return failure(error instanceof Error ? error.message : unavailable);
    } finally {
      pending = false;
    }
  };
})();
